/**
 * Enrichment attribute tooltip definitions + AttributeInfoIcon.
 *
 * A small "ⓘ" info icon (lucide `Info`) wrapped in the shared shadcn Tooltip.
 * Dropped next to any enrichment attribute chip / label so users understand
 * what each auto-discovered data point means and where it came from.
 */
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { SOURCE_LABELS } from "../utils/enrichmentContext";

/** Exact tooltip copy per enrichment attribute (matches product spec table). */
export const ATTRIBUTE_TOOLTIPS = {
  industry_sector:
    "The primary industry classification for this organization, based on its core business activities. Source: Context.dev industry taxonomy.",
  employee_size:
    "Estimated number of employees at this organization. Ranges like '51-200' indicate the confidence band from public data sources. Source: People Data Labs.",
  headquarters:
    "The primary office or headquarters location for this organization, including city, state/province, and country. Source: People Data Labs company records.",
  operating_locations:
    "Additional office locations beyond the headquarters where this organization operates. Source: People Data Labs.",
  regulatory_frameworks:
    "Industry-specific regulations, standards, and compliance frameworks that apply to this organization based on its sector and geography (e.g., ISO 22301, SOX, HIPAA). Source: Rules Engine analysis.",
  legal_name:
    "The official registered legal name of this organization as filed with regulatory authorities. Source: SEC EDGAR or ProPublica.",
  entity_type:
    "The organizational structure classification — e.g., Public Corporation, Private Company, Nonprofit, Government Agency. Source: People Data Labs or SEC EDGAR.",
  revenue_band:
    "Estimated annual revenue range for this organization. Source: People Data Labs or SEC EDGAR financial filings.",
  annual_revenue:
    "Reported or estimated annual revenue figure. For public companies this comes from SEC EDGAR XBRL filings; for private companies it is an estimate from People Data Labs.",
  naics_code:
    "North American Industry Classification System code — the standard U.S. government system for classifying businesses by industry. The 6-digit code identifies the specific industry segment. Source: Context.dev or People Data Labs.",
  naics_sector:
    "The broader industry sector and sub-sector derived from the NAICS code, providing a hierarchical view of where this organization fits in the economy. Source: Context.dev or People Data Labs.",
  products_services:
    "Key products and services offered by this organization, identified from public data sources. Used to generate company-specific business functions and risk assessments. Source: People Data Labs or AI Recon.",
  subsidiaries:
    "Known subsidiary companies or child organizations under this parent entity. Source: Wikidata or People Data Labs.",
  parent_company:
    "The immediate or ultimate parent organization, if this entity is a subsidiary. Source: People Data Labs or Wikidata.",
  company_summary:
    "A brief description of what this organization does, its market position, and key business characteristics. Source: People Data Labs or AI Recon.",
  industry_tags:
    "Additional keyword tags describing the organization's business focus areas beyond the primary NAICS classification. Source: People Data Labs.",
  tech_stack:
    "Key technology platforms and infrastructure used by this organization, identified from public data. Used to generate technology-related threats and infrastructure assessments. Source: People Data Labs or AI Recon.",
  ticker_symbol:
    "The stock market ticker symbol for publicly traded companies. Source: SEC EDGAR or People Data Labs.",
  wikidata_id:
    "The unique identifier for this organization in the Wikidata knowledge base (e.g., Q312 for Apple Inc.). Source: Wikidata SPARQL query.",
  geographic_hazards:
    "Location-specific natural disaster and environmental risks relevant to this organization's operating locations (e.g., flood zones, earthquake risk, hurricane exposure). Source: Rules Engine geographic analysis.",
};

/** Resolve tooltip text for an attribute, falling back to a generic definition. */
export function getAttributeTooltip(attribute, source) {
  if (attribute && ATTRIBUTE_TOOLTIPS[attribute]) return ATTRIBUTE_TOOLTIPS[attribute];
  const sourceName = (source && SOURCE_LABELS[source]) || source || "public data sources";
  return `Organization data point identified during auto-discovery. Source: ${sourceName}.`;
}

export function AttributeInfoIcon({ attribute, source, className = "", testId }) {
  const text = getAttributeTooltip(attribute, source);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label="More information"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className={`inline-flex items-center justify-center align-middle text-slate-400 hover:text-sky-600 cursor-help focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400 rounded-full transition-colors ${className}`}
            data-testid={testId || `attr-info-${attribute || "generic"}`}
          >
            <Info className="w-3 h-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-left text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default AttributeInfoIcon;
